import { Card } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

export type TileTone = 'neutral' | 'good' | 'warn' | 'critical' | 'info';

/** Literal class names (Tailwind only keeps classes it can read verbatim in the source). */
const VALUE_CLASS: Record<TileTone, string> = {
  neutral: 'text-ink',
  good: 'text-good',
  warn: 'text-warn',
  critical: 'text-critical',
  info: 'text-info',
};

export interface KpiTileProps {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: TileTone;
  /** When given, the whole card is a link (the filtered list the figure counts). */
  readonly to?: string;
  /** A stable handle for tests and for « scroll to » links. */
  readonly testId?: string;
}

/**
 * A very large amount (« 60 070 448,978 DT ») must wrap BEFORE its unit, never inside it (« D / T »): the space that ties a trailing unit to
 * the number is made breakable; the digits themselves stay glued by their narrow no-break spaces. A leading unit (Arabic) is left alone.
 */
const TRAILING_UNIT_GAP = /[\u00a0\u202f](?=[^\d\s\u00a0\u202f]+$)/u;
export const breakBeforeUnit = (value: string): string => value.replace(TRAILING_UNIT_GAP, ' ');

/**
 * One figure of the dashboard: label, value, a line of explanation. A card with a destination is ONE link whose accessible name says
 * both what it is and what it shows (« Non attribués : 41 »); a card without is plain text.
 */
export function KpiTile({
  label,
  value,
  hint,
  tone = 'neutral',
  to,
  testId,
}: KpiTileProps): ReactElement {
  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-inkFaint">{label}</p>
      <p
        className={`mt-1.5 font-display text-2xl font-bold [overflow-wrap:anywhere] ${VALUE_CLASS[tone]}`}
        dir="auto"
      >
        {breakBeforeUnit(value)}
      </p>
      {hint !== undefined ? <p className="mt-0.5 text-xs text-inkFaint">{hint}</p> : null}
    </>
  );

  if (to === undefined) {
    return (
      <Card className="p-4" data-testid={testId}>
        {body}
      </Card>
    );
  }

  return (
    <Link
      to={to}
      aria-label={`${label} : ${value}`}
      data-testid={testId}
      className="ecom-card block p-4 transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      {body}
    </Link>
  );
}
